import React, { createContext, useState, useContext, ReactNode, useMemo, useCallback } from 'react';

interface ResusContextType {
  age: number | null;
  weight: number | null;
  protocols: string[];
  hospital: string;
  setAge: (age: number) => void;
  setWeight: (weight: number | null) => void;
  setProtocols: (protocols: string[]) => void;
  updateContext: (age: number | null, weight: number | null, protocols: string[]) => void;
  resetContext: () => void;
}

const ResusContext = createContext<ResusContextType | undefined>(undefined);

export const useResusContext = () => {
  const context = useContext(ResusContext);
  if (!context) {
    throw new Error('useResusContext must be used within a ResusProvider');
  }
  return context;
};

interface ResusProviderProps {
  children: ReactNode;
  hospital: string;
}

export const ResusProvider: React.FC<ResusProviderProps> = ({ children, hospital }) => {
  const [state, setState] = useState<{ age: number | null; weight: number | null; protocols: string[] }>({
    age: null,
    weight: null,
    protocols: [],
  });

  // Memoized setter functions to prevent unnecessary re-renders
  const setAge = useCallback((age: number) => {
    setState((prev) => ({ ...prev, age }));
  }, []);

  const setWeight = useCallback((weight: number | null) => {
    setState((prev) => ({ ...prev, weight }));
  }, []);

  const setProtocols = useCallback((protocols: string[]) => {
    setState((prev) => ({ ...prev, protocols }));
  }, []);

  const updateContext = useCallback((age: number | null, weight: number | null, protocols: string[]) => {
    setState({ age, weight, protocols });
  }, []);

  const resetContext = useCallback(() => {
    setState({ age: null, weight: null, protocols: [] });
  }, []);

  // Memoizing context value to prevent unnecessary renders
  const contextValue = useMemo(() => ({
    ...state,
    hospital,
    setAge,
    setWeight,
    setProtocols,
    updateContext,
    resetContext,
  }), [state, hospital, setAge, setWeight, setProtocols, updateContext, resetContext]);

  return <ResusContext.Provider value={contextValue}>{children}</ResusContext.Provider>;
};